import { useMemo, useRef, useState } from 'react'
// Aliased: the bare name collides with the DOM global, and `verbatimModuleSyntax`
// means the type-only form has to be spelled out.
import type { KeyboardEvent as ReactKeyboardEvent } from 'react'
import Portrait from '../Portrait'
import { displayPosition, positionFill } from '../board/positions'
import { filterPool } from '../../lib/draftView'
import type { PlayerSummary } from '../../types/protocol'

// The draft room's primary surface. Everything else on the page is something
// you consult; this is the thing you operate.

const POSITIONS = ['ALL', 'QB', 'RB', 'WR', 'TE', 'K', 'DST'] as const

// Enough to fill a phone several times over without laying out the whole
// 1000-row pool on every keystroke. The old PickForm hard-capped at 50 with no
// way past it, which quietly hid every late-round kicker.
const PAGE = 60

interface Props {
  players: PlayerSummary[]
  draftedIds: Set<number | null>
  selectedId: number | null
  onSelect: (playerId: number) => void
  queue: number[]
  queueBusy: boolean
  canQueue: boolean
  onQueue: (playerId: number) => void
  onUnqueue: (playerId: number) => void
  hasCustomRankings: boolean
}

export default function PlayerPool({
  players,
  draftedIds,
  selectedId,
  onSelect,
  queue,
  queueBusy,
  canQueue,
  onQueue,
  onUnqueue,
  hasCustomRankings,
}: Props) {
  const [search, setSearch] = useState('')
  const [position, setPosition] = useState<string>('ALL')
  const [limit, setLimit] = useState(PAGE)
  const listRef = useRef<HTMLUListElement>(null)

  const available = useMemo(
    () => filterPool(players, { search, position, draftedIds }),
    [players, search, position, draftedIds],
  )

  const queued = useMemo(() => new Set(queue), [queue])
  const shown = available.slice(0, limit)

  function changeFilter(next: () => void) {
    next()
    setLimit(PAGE)
  }

  // Arrow keys walk the already-sorted pool, which is what makes the laptop's
  // scouting column worth keeping on screen: you read while scanning instead of
  // clicking twelve times to compare four running backs.
  //
  // On the section rather than window. QueuePanel registers dnd-kit's
  // KeyboardSensor, which owns the arrows whenever a drag handle has focus; a
  // window listener would reorder the queue *and* move the pool selection on one
  // keypress, and hijack arrows in every other input on the page.
  function onKeyDown(event: ReactKeyboardEvent<HTMLElement>) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
    // Browser and OS shortcuts stay theirs.
    if (event.metaKey || event.ctrlKey || event.altKey) return
    // Guard the empty list BEFORE preventDefault. With a search matching nothing
    // `shown` is [], shown[0] is undefined, and both the selection and the
    // querySelector below would go looking for "undefined" — and swallowing the
    // arrow there would kill page scroll in exchange for doing nothing.
    if (shown.length === 0) return
    event.preventDefault()

    // changeFilter resets `limit` but deliberately does not reset the selection,
    // so -1 means either nothing is selected or a filter moved it out of view.
    const i = shown.findIndex((p) => p.id === selectedId)
    let nextId: number

    if (i === -1) {
      nextId = shown[0].id
    } else if (event.key === 'ArrowDown') {
      if (i < shown.length - 1) {
        nextId = shown[i + 1].id
      } else if (available.length > shown.length) {
        // The end of `shown` is a pagination boundary, not the end of the pool —
        // PAGE is 60 and "Show more" is the only other way past it.
        setLimit((current) => current + PAGE)
        nextId = available[i + 1].id
      } else {
        return
      }
    } else {
      if (i === 0) return
      nextId = shown[i - 1].id
    }

    onSelect(nextId)
    // rAF because the row may live in the slice we just unlocked with setLimit —
    // it is not in the DOM until React commits.
    requestAnimationFrame(() => {
      listRef.current
        ?.querySelector(`[data-player-id="${nextId}"]`)
        ?.scrollIntoView({ block: 'nearest' })
    })
  }

  return (
    <section className="flex min-w-0 flex-col" onKeyDown={onKeyDown}>
      {/* The filter block and the list are one card at lg and two bare stacks on
          a phone, where chrome around the primary surface is wasted width. */}
      <div className="py-2.5 lg:rounded-t-lg lg:border-x lg:border-t lg:border-line lg:bg-surface-1 lg:px-4 lg:shadow-panel">
        <label className="flex items-center gap-2 rounded-md border border-line bg-surface-3 px-3 py-2 shadow-[inset_0_1px_2px_rgb(0_0_0/0.4)] focus-within:border-signal/60">
          <span aria-hidden="true" className="text-ink-lo">
            ⌕
          </span>
          <input
            type="search"
            value={search}
            onChange={(event) => changeFilter(() => setSearch(event.target.value))}
            placeholder="Search players"
            aria-label="Search players"
            className="min-w-0 flex-1 bg-transparent text-sm text-ink-hi placeholder:text-ink-lo focus:outline-none"
          />
          {search && (
            <button
              type="button"
              onClick={() => changeFilter(() => setSearch(''))}
              aria-label="Clear search"
              className="text-ink-lo hover:text-ink-hi"
            >
              ✕
            </button>
          )}
        </label>

        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {POSITIONS.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={position === option}
              onClick={() => changeFilter(() => setPosition(option))}
              className={`rounded-full px-2.5 py-0.5 font-mono text-[11px] font-semibold tracking-wide ${
                position === option
                  ? 'bg-signal text-ink shadow-raised'
                  : 'border border-line text-ink-lo hover:border-line-strong hover:text-ink-mid'
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      </div>

      <div className="lg:rounded-b-lg lg:border-x lg:border-b lg:border-line lg:bg-surface-1 lg:px-2 lg:pb-2">
        {shown.length === 0 ? (
          <p className="px-1 py-10 text-center text-sm text-ink-lo">
            {players.length === 0
              ? 'Loading the player pool…'
              : 'No players match that. Try a different position or clear the search.'}
          </p>
        ) : (
          <ul ref={listRef} className="mt-1">
            {shown.map((player) => (
              <PoolRow
                key={player.id}
                player={player}
                selected={player.id === selectedId}
                queued={queued.has(player.id)}
                canQueue={canQueue}
                queueBusy={queueBusy}
                hasCustomRankings={hasCustomRankings}
                onSelect={onSelect}
                onQueue={onQueue}
                onUnqueue={onUnqueue}
              />
            ))}
          </ul>
        )}

        {available.length > shown.length && (
          <button
            type="button"
            onClick={() => setLimit((current) => current + PAGE)}
            className="mt-2 w-full rounded-md border border-line py-2.5 text-sm font-medium text-ink-lo hover:border-line-strong hover:bg-surface-2 hover:text-ink-mid"
          >
            Show more · {available.length - shown.length} left
          </button>
        )}
      </div>
    </section>
  )
}

function PoolRow({
  player,
  selected,
  queued,
  canQueue,
  queueBusy,
  hasCustomRankings,
  onSelect,
  onQueue,
  onUnqueue,
}: {
  player: PlayerSummary
  selected: boolean
  queued: boolean
  canQueue: boolean
  queueBusy: boolean
  hasCustomRankings: boolean
  onSelect: (playerId: number) => void
  onQueue: (playerId: number) => void
  onUnqueue: (playerId: number) => void
}) {
  return (
    // The anchor the keyboard walk scrolls to, and the scroll-margin that keeps
    // it clear of the chrome: the header stack is sticky top-0 and PickBar is
    // pinned to the bottom, so `block: 'nearest'` alone lands rows underneath
    // both. Scroll-margin is not layout — it costs nothing visually.
    <li data-player-id={player.id} className="scroll-mt-32 scroll-mb-44">
      <div
        className={`flex items-center gap-2.5 rounded-md border px-2 py-1.5 ${
          selected
            ? 'border-signal/50 bg-signal/10 shadow-[inset_2px_0_0_var(--color-signal)]'
            : 'border-transparent hover:bg-surface-2'
        }`}
      >
        <button
          type="button"
          onClick={() => onSelect(player.id)}
          aria-pressed={selected}
          className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
        >
          <span className="w-5 shrink-0 text-right font-mono text-[11px] text-ink-lo tabular-nums">
            <Rank player={player} hasCustomRankings={hasCustomRankings} />
          </span>
          <Portrait
            fullName={player.full_name}
            position={player.position}
            nflTeam={player.nfl_team}
            src={player.portrait_url}
            size="sm"
          />
          <span
            className={`${positionFill(player.position)} text-pos-ink w-8 shrink-0 rounded-sm py-0.5 text-center font-mono text-[10px] font-semibold`}
          >
            {displayPosition(player.position)}
          </span>
          <span className="min-w-0 flex-1 truncate text-sm text-ink-hi">{player.full_name}</span>
          <span className="w-7 shrink-0 font-mono text-[10.5px] text-ink-lo">
            {player.nfl_team ?? 'FA'}
          </span>
          {/* Its own fixed column, like every other cell in the row: the ~300
              players with no number would otherwise shift the name's truncation
              point row to row. Too small a plate for a badge here — see JERSEY
              in Portrait. */}
          <span className="w-6 shrink-0 font-mono text-[10.5px] text-ink-lo tabular-nums">
            {player.jersey_number != null && `#${player.jersey_number}`}
          </span>
          {/* Fixed column for the same reason as the jersey: free agents and an
              un-synced pool would otherwise move the name's truncation point
              from row to row. */}
          <span className="w-10 shrink-0 font-mono text-[10.5px] text-ink-lo tabular-nums">
            {player.bye_week != null && `BYE ${player.bye_week}`}
          </span>
        </button>

        {canQueue && (
          <button
            type="button"
            disabled={queueBusy}
            onClick={() => (queued ? onUnqueue(player.id) : onQueue(player.id))}
            aria-label={queued ? `Remove ${player.full_name} from queue` : `Add ${player.full_name} to queue`}
            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-sm border text-xs disabled:opacity-40 ${
              queued
                ? 'border-signal/50 bg-signal/10 text-signal'
                : 'border-line text-ink-lo hover:border-line-strong hover:text-ink-mid'
            }`}
          >
            {queued ? '✓' : '+'}
          </button>
        )}
      </div>
    </li>
  )
}

// With a CSV loaded, a bare number next to a player the file never listed
// reads like a rank from that file. Parenthesized means "this is the default
// rank, not yours".
function Rank({
  player,
  hasCustomRankings,
}: {
  player: PlayerSummary
  hasCustomRankings: boolean
}) {
  if (!hasCustomRankings || player.custom_rank) return <>{player.effective_rank}</>
  return (
    <span title="Not in your rankings file — default rank">({player.effective_rank})</span>
  )
}
